import {  useState, useEffect } from 'react';
import { ref, onValue, push, set } from 'firebase/database';
import { db } from '../firebase';
import Popup from './Popup';

const BiddingPanel = ({ gameId, teamId, teamName }) => {
    const [currentPlayer, setCurrentPlayer] = useState(null);
    const [status, setStatus] = useState('upcoming');
    const [bidAmount, setBidAmount] = useState('');
    const [loading, setLoading] = useState(false);
    const [team, setTeam] = useState(null);
    const [bids, setBids] = useState([]);
    const [highestBid, setHighestBid] = useState(null);
    const [myBid, setMyBid] = useState(null);
    const [auctionSettings, setAuctionSettings] = useState(null);
    const [popup, setPopup] = useState({ isOpen: false, message: '', title: '', type: 'info' });

    useEffect(() => {
        if (!gameId) return;

        const unsubscribers = [];

        // Listen to status
        const statusRef = ref(db, `games/${gameId}/details/status`);
        unsubscribers.push(onValue(statusRef, (snapshot) => {
            if (snapshot.exists()) {
                setStatus(snapshot.val());
            }
        }));

        // Listen to auction settings
        const settingsRef = ref(db, `games/${gameId}/auctionSettings`);
        unsubscribers.push(onValue(settingsRef, (snapshot) => {
            if (snapshot.exists()) {
                setAuctionSettings(snapshot.val());
            }
        }));

        // Listen to current player
        const currentPlayerRef = ref(db, `games/${gameId}/currentPlayer`);
        unsubscribers.push(onValue(currentPlayerRef, (snapshot) => {
            if (snapshot.exists()) {
                const player = snapshot.val();
                setCurrentPlayer(player);
                const initial = Number(player.currentBid ?? player.price ?? 0) || 0;
                setBidAmount(initial);
            } else {
                setCurrentPlayer(null);
            }
        }));

        // Listen to team details
        if (teamId) {
            const teamRef = ref(db, `games/${gameId}/teams/${teamId}`);
            unsubscribers.push(onValue(teamRef, (snapshot) => {
                if (snapshot.exists()) {
                    setTeam(snapshot.val());
                }
            }));
        }

        return () => {
            unsubscribers.forEach(unsub => unsub());
        };
    }, [gameId, teamId]);

    useEffect(() => {
        if (!gameId) return;

        // Listen to bids
        const bidsRef = ref(db, `games/${gameId}/currentBids`);
        const unsubscribeBids = onValue(bidsRef, (snapshot) => {
            if (snapshot.exists()) {
                const bidsData = snapshot.val();
                const bidsArray = Object.entries(bidsData).map(([id, bid]) => ({
                    ...bid,
                    id,
                }));
                bidsArray.sort((a, b) => b.amount - a.amount);
                setBids(bidsArray);
                setHighestBid(bidsArray[0] || null);

                // Find my bid
                const myBidData = bidsArray.find((b) => b.teamId === teamId);
                setMyBid(myBidData || null);

                // Auto-update bid amount to be higher than highest bid
                if (bidsArray.length > 0 && bidsArray[0].teamId !== teamId) {
                    const minIncrement = Number(auctionSettings?.minBidIncrement || 1000);
                    setBidAmount(Number(bidsArray[0].amount) + minIncrement);
                }
            } else {
                setBids([]);
                setHighestBid(null);
                setMyBid(null);
            }
        });

        return () => unsubscribeBids();
    }, [gameId, teamId, auctionSettings]);

    const handlePlaceBid = async (e) => {
        if (e) e.preventDefault();

        if (!currentPlayer) {
            setPopup({
                isOpen: true,
                title: 'No Player Available',
                message: 'No player currently on auction!',
                type: 'warning'
            });
            return;
        }

        const amount = Number(bidAmount);

        if (!amount || amount <= 0) {
            setPopup({
                isOpen: true,
                title: 'Invalid Bid Amount',
                message: 'Please enter a valid bid amount!',
                type: 'warning'
            });
            return;
        }

        // Check if bid is higher than base price
        if (amount < Number(currentPlayer.price)) {
            setPopup({
                isOpen: true,
                title: 'Bid Too Low',
                message: `Bid must be at least ৳${Number(currentPlayer.price).toLocaleString()}!`,
                type: 'warning'
            });
            return;
        }
        if(amount > Number(auctionSettings?.maxAmountPerPlayer || Infinity)){
            setPopup({
                isOpen: true,
                title: 'Bid Too High',
                message: `Bid must not exceed maximum allowed ৳${Number(auctionSettings?.maxAmountPerPlayer).toLocaleString()}!`,
                type: 'warning'
            });
            return;
        }

        // Check if bid is higher than current highest bid
        if (highestBid && amount <= Number(highestBid.amount)) {
            setPopup({
                isOpen: true,
                title: 'Bid Not High Enough',
                message: `Bid must be higher than ৳${Number(highestBid.amount).toLocaleString()}!`,
                type: 'warning'
            });
            return;
        }

        // Check minimum increment
        const minIncrement = Number(auctionSettings?.minBidIncrement || 1000);
        if (highestBid && amount < Number(highestBid.amount) + minIncrement) {
            setPopup({
                isOpen: true,
                title: 'Minimum Increment Required',
                message: `Minimum bid increment is ৳${minIncrement.toLocaleString()}. Bid must be at least ৳${(
                    Number(highestBid.amount) + minIncrement
                ).toLocaleString()}`,
                type: 'warning'
            });
            return;
        }

        // Check team purse
        if (!team || amount > Number(team.purseLeft)) {
            setPopup({
                isOpen: true,
                title: 'Insufficient Funds',
                message: `Insufficient purse! Available: ৳${Number(team?.purseLeft).toLocaleString()}`,
                type: 'error'
            });
            return;
        }

        setLoading(true);
        try {
            const bidRef = push(ref(db, `games/${gameId}/currentBids`));
            await set(bidRef, {
                teamId: teamId,
                teamName: teamName,
                amount: amount,
                playerId: currentPlayer.id,
                playerName: currentPlayer.name,
                timestamp: new Date().toISOString(),
            });

            // Update current player's current bid
            await set(ref(db, `games/${gameId}/currentPlayer/currentBid`), amount);

            // Log the bid
            const logRef = push(ref(db, `games/${gameId}/logs`));
            await set(logRef, {
                action: "BID_PLACED",
                details: {
                    teamId: teamId,
                    teamName: teamName,
                    amount: amount,
                    playerId: currentPlayer.id,
                    playerName: currentPlayer.name,
                },
                timestamp: new Date().toISOString(),
            });

            // Increment for next bid
            const nextBidAmount = amount + Number(minIncrement);
            setBidAmount(nextBidAmount);
        } catch (error) {
            console.error('Error placing bid:', error);
            setPopup({
                isOpen: true,
                title: 'Bid Failed',
                message: 'Failed to place bid: ' + error.message,
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    if (status !== 'live') {
        return (
            <div className="card shadow">
                <div className="card-body p-4 text-center">
                    <i className="bi bi-clock-history display-4 text-muted"></i>
                    <h4 className="mt-3">Auction Not Active</h4>
                    <p className="text-muted">
                        Current Status:{" "}
                        <span className="badge bg-secondary fs-6">{status}</span>
                    </p>
                    <p className="mb-0">
                        Please wait for the organizer to start the auction.
                    </p>
                </div>
            </div>
        );
    }

    if (!currentPlayer) {
        return (
            <div className="card shadow">
                <div className="card-body p-4 text-center">
                    <i className="bi bi-hourglass-split display-4 text-warning"></i>
                    <h4 className="mt-3">Waiting for Player</h4>
                    <p className="text-muted mb-0">
                        The organizer hasn't selected a player yet.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="card shadow">
            <div className="card-header bg-gradient text-white" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
                <h4 className="mb-0">
                    <i className="bi bi-hammer me-2"></i>
                    Live Bidding Panel
                </h4>
            </div>

            <div className="card-body p-4">
                {/* Current Player */}
                <div className="card mb-4 border-primary shadow-sm">
                    <div className="card-header bg-primary text-white">
                        <h5 className="mb-0">
                            <i className="bi bi-person-circle me-2"></i>
                            Current Player on Auction
                        </h5>
                    </div>
                    <div className="card-body">
                        <div className="row align-items-center">
                            <div className="col-md-4 text-center">
                                
                                    <img
                                        src={currentPlayer.image || "/player.png"}
                                        alt={currentPlayer.name}
                                        className="img-fluid rounded shadow"
                                        style={{ maxHeight: "350px", objectFit: "cover" }}
                                    />
                                
                            </div>
                            <div className="col-md-8">
                                <h3 className="mb-3">{currentPlayer.name}</h3>
                                <div className="row">
                                    <div className="col-6 mb-2">
                                        <strong>Role:</strong>
                                        <br />
                                        <span className="badge bg-info fs-6">
                                            {currentPlayer.role}
                                        </span>
                                    </div>
                                    <div className="col-6 mb-2">
                                        <strong>Base Price:</strong>
                                        <br />
                                        <span className="text-success fs-5 fw-bold">
                                            ৳{Number(currentPlayer.price || 0).toLocaleString()}
                                        </span>
                                    </div>
                                    {currentPlayer.playerNumber && (
                                        <div className="col-6 mb-2">
                                            <strong>Jersey Number:</strong>
                                            <br />
                                            <span className="badge bg-dark fs-6">
                                                #{currentPlayer.playerNumber}
                                            </span>
                                        </div>
                                    )}
                                    {currentPlayer.age && (
                                        <div className="col-6 mb-2">
                                            <strong>Age:</strong>
                                            <br />
                                            <span>{currentPlayer.age} years</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Team Info */}
                {team && (
                    <div className="alert alert-info mb-4">
                        <div className="row align-items-center">
                            <div className="col-md-6">
                                <strong>
                                    <i className="bi bi-shield-fill me-2"></i>
                                    Your Team:
                                </strong>
                                <div className="fs-5 fw-bold">{teamName}</div>
                            </div>
                            <div className="col-md-6">
                                <strong>
                                    <i className="bi bi-wallet2 me-2"></i>
                                    Purse Left:
                                </strong>
                                <div className="fs-4 text-success fw-bold">
                                    ৳{Number(team.purseLeft).toLocaleString()}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Current Bidding Status */}
                {highestBid && (
                    <div
                        className={`alert ${
                            myBid && myBid.id === highestBid.id
                                ? "alert-success"
                                : "alert-warning"
                        } mb-4`}
                    >
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <strong>
                                    <i className="bi bi-trophy-fill me-2"></i>
                                    Highest Bid:
                                </strong>
                                <div className="display-6">
                                    ৳{Number(highestBid.amount).toLocaleString()}
                                </div>
                                <small className="text-muted">by {highestBid.teamName}</small>
                            </div>
                            {myBid && myBid.id === highestBid.id && (
                                <div className="text-center">
                                    <i className="bi bi-check-circle-fill display-1 text-success"></i>
                                    <div className="badge bg-success fs-6 mt-2">
                                        You're Leading!
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* My Current Bid */}
                {myBid && myBid.id !== highestBid?.id && (
                    <div className="alert alert-light border mb-4">
                        <strong>
                            <i className="bi bi-hand-index-thumb me-2"></i>
                            Your Current Bid:
                        </strong>{" "}
                        <span className="fs-5 fw-bold text-primary">
                            ৳{Number(myBid.amount).toLocaleString()}
                        </span>
                        <span className="badge bg-danger ms-2">Not Leading</span>
                    </div>
                )}

                {/* Bid Amount Display & Controls */}
                <div className="card border-primary mb-4">
                    <div className="card-body text-center p-3">
                        <label className="form-label fw-semibold fs-6 mb-2">
                            <i className="bi bi-currency-dollar me-2"></i>
                            Your Bid Amount
                        </label>
                        
                        <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                            <button
                                className="btn btn-danger rounded-circle"
                                style={{ width: '45px', height: '45px', fontSize: '1.25rem' }}
                                onClick={() => {
                                    const minIncrement = Number(auctionSettings?.minBidIncrement || 1000);
                                    const minAmount = Number(currentPlayer?.price || 0);
                                    const newAmount = Math.max(minAmount, Number(bidAmount) - minIncrement);
                                    setBidAmount(newAmount);
                                }}
                                disabled={loading || Number(bidAmount) <= Number(currentPlayer?.price || 0)}
                            >
                                <i className="bi bi-dash-lg"></i>
                            </button>

                            <div className="px-3 py-2 bg-light rounded" style={{ minWidth: '180px' }}>
                                <div className="fs-3 fw-bold text-success">
                                    ৳{Number(bidAmount).toLocaleString()}
                                </div>
                            </div>

                            <button
                                className="btn btn-success rounded-circle"
                                style={{ width: '45px', height: '45px', fontSize: '1.25rem' }}
                                onClick={() => {
                                    const minIncrement = Number(auctionSettings?.minBidIncrement || 1000);
                                    const maxAmount = Number(auctionSettings?.maxAmountPerPlayer || Infinity);
                                    const newAmount = Math.min(maxAmount, Number(bidAmount) + minIncrement);
                                    setBidAmount(newAmount);
                                }}
                                disabled={loading || (auctionSettings?.maxAmountPerPlayer && Number(bidAmount) >= Number(auctionSettings.maxAmountPerPlayer))}
                            >
                                <i className="bi bi-plus-lg"></i>
                            </button>
                        </div>

                        {auctionSettings?.minBidIncrement && (
                            <small className="text-muted d-block mb-2" style={{ fontSize: '0.8rem' }}>
                                <i className="bi bi-info-circle me-1"></i>
                                Each click adjusts by ৳{Number(auctionSettings.minBidIncrement).toLocaleString()}
                            </small>
                        )}
                        
                        {auctionSettings?.maxAmountPerPlayer && (
                            <small className="text-muted d-block mb-2" style={{ fontSize: '0.8rem' }}>
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                Maximum allowed: ৳{Number(auctionSettings.maxAmountPerPlayer).toLocaleString()}
                            </small>
                        )}

                        <button
                            onClick={handlePlaceBid}
                            className="btn btn-primary w-100 shadow-sm"
                            disabled={loading || !bidAmount}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Placing Bid...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-hand-thumbs-up-fill me-2"></i>
                                    Place Bid - ৳{Number(bidAmount).toLocaleString()}
                                </>
                            )}
                        </button>
                    </div>
                </div>

                                {/* Bidding Tips */}
                <div className="alert alert-light mt-4">
                    <h6 className="alert-heading">
                        <i className="bi bi-lightbulb me-2"></i>
                        Bidding Tips:
                    </h6>
                    <ul className="mb-0 small">
                        <li>Your bid must be higher than the current highest bid</li>
                        <li>Make sure you have sufficient purse left</li>
                        <li>
                            Minimum increment: ৳
                            {Number(auctionSettings?.minBidIncrement || 1000).toLocaleString()}
                        </li>
                        <li>Once organizer confirms, the player will be assigned to the winning team</li>
                    </ul>
                </div>

                {/* All Bids */}
                {bids.length > 0 && (
                    <div className="mt-4">
                        <h5 className="mb-3">
                            <i className="bi bi-list-ol me-2"></i>
                            All Bids ({bids.length})
                        </h5>
                        <div className="list-group">
                            {bids.map((bid, index) => (
                                <div
                                    key={bid.id}
                                    className={`list-group-item ${
                                        bid.teamId === teamId
                                            ? "list-group-item-success border-success"
                                            : ""
                                    } ${index === 0 ? "border-warning border-3" : ""}`}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong className="fs-5">
                                                {index === 0 && (
                                                    <i className="bi bi-trophy-fill text-warning me-2"></i>
                                                )}
                                                {index + 1}. {bid.teamName}
                                            </strong>
                                            {bid.teamId === teamId && (
                                                <span className="badge bg-success ms-2">You</span>
                                            )}
                                            {index === 0 && (
                                                <span className="badge bg-warning text-dark ms-2">
                                                    Leading
                                                </span>
                                            )}
                                            <br />
                                            <small className="text-muted">
                                                {new Date(bid.timestamp).toLocaleString()}
                                            </small>
                                        </div>
                                        <div>
                                            <span
                                                className={`fs-4 fw-bold ${
                                                    index === 0 ? "text-warning" : "text-success"
                                                }`}
                                            >
                                                ৳{Number(bid.amount).toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {bids.length === 0 && (
                    <div className="alert alert-info mt-4">
                        <i className="bi bi-info-circle me-2"></i>
                        No bids yet. Be the first to place a bid!
                    </div>
                )}
            </div>

            <Popup
                isOpen={popup.isOpen}
                onClose={() => setPopup({ isOpen: false, message: '', title: '', type: 'info' })}
                message={popup.message}
                title={popup.title}
                type={popup.type}
            />
        </div>
    );
};

export default BiddingPanel;